import apiClient from "./apiClient";
import { useAuthStore } from "../stores/authStore";
import { clearSession, getSessionUser } from "./authSession";
import { formatApiError } from "../lib/laravelError";

function normalizeUser(apiUser) {
  if (!apiUser || typeof apiUser !== "object") return null;
  const name = apiUser.name ?? apiUser.nick ?? "";
  return {
    ...apiUser,
    nick: String(name || apiUser.nick || ""),
  };
}

export const authService = {
  async login({ email, password }) {
    clearSession();
    const device_name =
      typeof navigator !== "undefined"
        ? String(navigator.userAgent || "zyrisx-web").slice(0, 255)
        : "zyrisx-web";
    try {
      const { data } = await apiClient.post("/login", {
        email,
        password,
        device_name,
      });
      const token = data?.token;
      if (!token) {
        const err = new Error("Respuesta inválida del servidor (sin token)");
        throw err;
      }
      const user = normalizeUser(data.user);
      useAuthStore.getState().setAuth(user, String(token));
      return user;
    } catch (err) {
      clearSession();
      const msg = formatApiError(err, "No se pudo iniciar sesión");
      const e = new Error(msg);
      e.response = err.response;
      throw e;
    }
  },

  /**
   * Alta de lead en la API (no en el origen del SPA).
   * Petición: POST `{REACT_APP_API_BASE_URL}/lead` → p. ej. `https://api.zyrisx.com/v1/lead`
   * Body JSON: `{ "email": "..." }`. Cabeceras: Accept y Content-Type application/json (las pone `apiClient`).
   */
  async register({ email }) {
    try {
      const { data } = await apiClient.post("/lead", { email });
      return data;
    } catch (err) {
      const e = new Error(formatApiError(err, "No se pudo enviar la solicitud"));
      e.response = err.response;
      throw e;
    }
  },

  async logout() {
    try {
      if (useAuthStore.getState().token) {
        await apiClient.post("/logout");
      }
    } catch {
      // Continuar: limpiar siempre en cliente
    } finally {
      clearSession();
    }
  },

  async forgotPassword({ email }) {
    try {
      const { data } = await apiClient.post("/forgot-password", { email });
      return {
        ok: true,
        message:
          data?.message ||
          "Si el email está registrado, recibirás instrucciones en breve.",
      };
    } catch (err) {
      throw new Error(formatApiError(err, "No se pudo procesar la solicitud"));
    }
  },

  getCurrentUser() {
    return getSessionUser();
  },

  isAuthenticated() {
    return !!useAuthStore.getState().token;
  },

  async getForgotPasswordInfo() {
    const { data } = await apiClient.get("/forgot-password");
    return data;
  },

  async getResetPasswordMeta(token) {
    const { data } = await apiClient.get(`/reset-password/${encodeURIComponent(token)}`);
    return data;
  },

  async resetPassword({ token, email, password, password_confirmation }) {
    try {
      const { data } = await apiClient.post("/reset-password", {
        token,
        email,
        password,
        password_confirmation,
      });
      return data;
    } catch (err) {
      throw new Error(formatApiError(err, "No se pudo restablecer la contraseña"));
    }
  },

  async resendVerificationEmail() {
    try {
      await apiClient.post("/email/verification-notification");
    } catch (err) {
      throw new Error(formatApiError(err, "No se pudo reenviar el correo"));
    }
  },
};
